import { useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogRoot,
  DialogTitle,
  Input,
  InputHelperText,
  InputLabel,
  InputRoot,
  Label,
  Skeleton,
} from '@mint-lib/tw-components';
import { DateRange } from '@mint-lib/tw-components/patterns';

import { useMediaPlan } from '../../../../api/queries/useMediaPlan.js';
import { ActivityDateRange } from './ActivityDateRange.jsx';
import {
  CreateActivityData,
  getCreateActivityValidationSchema,
  useCreateActivity,
} from './CreateActivityModal.utils.js';
import { CurrencyInput } from './CurrencyInput.jsx';

type CreateActivityModalProps = {
  onClose: () => void;
};

const CREATE_ACTIVITY_FORM_ID = 'create-activity-form';

const INITIAL_DATE_RANGE: DateRange = { from: undefined };

export const CreateActivityModal = ({ onClose }: CreateActivityModalProps) => {
  const { t } = useTranslation('@myn/planning_v2');

  const createAnotherCheckboxId = useId();
  const nameInputId = useId();
  const budgetInputId = useId();

  const [shouldCreateAnother, setShouldCreateAnother] = useState(false);

  const { data: mediaPlan } = useMediaPlan();

  const { control, handleSubmit, formState, reset } =
    useForm<CreateActivityData>({
      defaultValues: {
        name: '',
        date: INITIAL_DATE_RANGE,
        budget: null,
      },
      resolver: zodResolver(getCreateActivityValidationSchema(t)),
    });

  const [dateRange, setDateRange] = useState<DateRange>(INITIAL_DATE_RANGE);

  const {
    createActivity,
    resetValidationErrors,
    nameValidationError,
    dateRangeValidationError,
  } = useCreateActivity();

  const onSubmit = async (data: CreateActivityData) => {
    resetValidationErrors();
    await createActivity(data);

    if (shouldCreateAnother) {
      reset();
      setDateRange(INITIAL_DATE_RANGE);

      return;
    }

    onClose();
  };

  return (
    <Dialog modal open>
      <DialogOverlay onClick={onClose} />
      <DialogRoot size="m">
        <DialogHeader onClose={onClose}>
          <DialogTitle>{t('New activity')}</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <form
            id={CREATE_ACTIVITY_FORM_ID}
            onSubmit={handleSubmit(onSubmit)}
            className="flex w-full flex-col gap-3"
          >
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => {
                const hasError = Boolean(
                  fieldState.error || nameValidationError,
                );

                return (
                  <InputRoot>
                    <InputLabel label={t('Name*')} htmlFor={nameInputId} />
                    <Input
                      id={nameInputId}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('Enter name')}
                      color={hasError ? 'error' : 'default'}
                      aria-label="Enter activity name"
                    />
                    {hasError && (
                      <InputHelperText
                        color="error"
                        helperText={
                          fieldState.error?.message ?? nameValidationError
                        }
                      />
                    )}
                  </InputRoot>
                );
              }}
            />
            <Controller
              control={control}
              name="date"
              render={({ field, fieldState }) => (
                <ActivityDateRange
                  control={control}
                  error={fieldState.error}
                  dateRangeValidationError={dateRangeValidationError}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  onApply={field.onChange}
                />
              )}
            />
            {mediaPlan ? (
              <Controller
                control={control}
                name="budget"
                render={({ field, fieldState }) => {
                  return (
                    <InputRoot>
                      <InputLabel
                        label={t('Activity budget')}
                        htmlFor={budgetInputId}
                      />
                      <CurrencyInput
                        id={budgetInputId}
                        currency={mediaPlan.currency}
                        value={field.value}
                        onChange={field.onChange}
                        color={fieldState.error ? 'error' : 'default'}
                        aria-label="Enter activity budget"
                      />
                      {fieldState.error?.message && (
                        <InputHelperText
                          color="error"
                          helperText={fieldState.error.message}
                        />
                      )}
                    </InputRoot>
                  );
                }}
              />
            ) : (
              <Skeleton className="h-15" />
            )}
          </form>
        </DialogContent>
        <DialogFooter>
          <Label
            className="flex-1 gap-2"
            htmlFor={createAnotherCheckboxId}
            variant="bodyLong01"
          >
            <Checkbox
              id={createAnotherCheckboxId}
              checked={shouldCreateAnother}
              onCheckedChange={() => {
                setShouldCreateAnother((checked) => !checked);
              }}
            />
            <span>{t('Create another')}</span>
          </Label>
          <Button
            size="large"
            variant="text"
            onClick={onClose}
            aria-label="Close create activity modal"
          >
            {t('Cancel')}
          </Button>
          <Button
            size="large"
            color="primary"
            type="submit"
            form={CREATE_ACTIVITY_FORM_ID}
            disabled={!formState.isDirty || formState.isSubmitting}
            aria-label="Submit create activity modal"
          >
            {t('Save')}
          </Button>
        </DialogFooter>
      </DialogRoot>
    </Dialog>
  );
};
